import React, { useState } from "react";
import { BusinessDetails } from '@/types/index';
import { UnifiedPayrollDashboard } from './UnifiedPayrollDashboard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DollarSign } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PayrollTabProps {
  business: BusinessDetails | null;
  loadingData: boolean;
  onBusinessUpdate?: (updates: Partial<BusinessDetails>) => void;
}

const PayrollTab: React.FC<PayrollTabProps> = ({ business, loadingData, onBusinessUpdate }) => {
  const [enabling, setEnabling] = useState(false);

  const handleEnablePayments = async () => {
    if (!business?.id) return;

    setEnabling(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ payment_processing_enabled: true })
        .eq('id', business.id);

      if (error) throw error;

      // Update local state through callback
      onBusinessUpdate?.({ payment_processing_enabled: true });

      toast.success("Payment system enabled", {
        description: "Payroll and billing features are now active"
      });
    } catch (error) {
      console.error('Error enabling payments:', error);
      toast.error("Failed to enable payments", {
        description: "Please try again or check your connection"
      });
    } finally {
      setEnabling(false);
    }
  };

  // Check for no business first (before payment check)
  if (!business) {
    return (
      <div className="text-center py-8">
        <p>Please select a business to view payroll data.</p>
      </div>
    );
  }

  if (loadingData) {
    return (
      <div className="text-center py-8">
        <p>Loading payroll data...</p>
      </div>
    );
  }

  if (!business.payment_processing_enabled) {
    return (
      <div className="p-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Payroll Management
            </CardTitle>
            <CardDescription>Manage employee payroll and calculations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-foreground">Payment System is Disabled</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  The payment system is currently turned off. Enable payments to access payroll management,
                  calculations, and billing features.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="default"
                  onClick={handleEnablePayments}
                  disabled={enabling}
                >
                  {enabling ? "Enabling..." : "Enable Payments"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  or configure in Business Settings
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pass business details with country_code to UnifiedPayrollDashboard
  return (
    <UnifiedPayrollDashboard
      business={business}
      loadingData={loadingData}
    />
  );
};

export default PayrollTab;
